import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuthInfo } from '@propelauth/react';
import { usePostHog } from 'posthog-js/react';
import { config } from '../config';
import { TrackingIds } from '../utils/analytics';

export interface Notification {
  id: string;
  message: string;
  timestamp: string;
  read: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'read'>) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  isConnected: boolean;
  requestPermission: () => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const { accessToken, isLoggedIn } = useAuthInfo();
  const posthog = usePostHog();

  // Add a new notification
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      read: false,
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    
    // Show browser notification if permission is granted
    if (Notification.permission === 'granted') {
      new Notification('Haven', {
        body: notification.message,
        icon: '/haven-logo-new.png',
      });
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // Connect to Server-Sent Events for real-time notifications
  useEffect(() => {
    let eventSource: EventSource | null = null;

    const connectToNotifications = async () => {
      try {
        // Check if user is logged in and has access token
        if (!isLoggedIn || !accessToken) {
          // Only log in development mode to reduce noise
          if (import.meta.env.MODE === 'development') {
            console.log('No access token found, skipping notification connection');
          }
          return;
        }

        // Create EventSource connection with proper headers
        // Note: EventSource doesn't support custom headers, so we need to use a different approach
        // For now, we'll use the token as a query parameter but the backend should validate it properly
        const notificationUrl = `${config.API_URL}/notifications/stream?token=${accessToken}`;
        // Only log connection URL in development mode
        if (import.meta.env.MODE === 'development') {
          console.log('Connecting to notification stream:', notificationUrl);
        }
        eventSource = new EventSource(notificationUrl);
        
        eventSource.onopen = () => {
          // Only log connection in development mode to reduce noise
          if (import.meta.env.MODE === 'development') {
            console.log('Connected to notification stream');
          }
          setIsConnected(true);
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Only log in development mode to reduce noise
            if (import.meta.env.MODE === 'development') {
              console.log('Received notification:', data);
              console.log('Notification type:', data.type);
            }
            
            if (data.type === 'adaptation_plan_ready') {
              addNotification({
                message: 'Your climate adaptation report is ready! Click to view your personalized recommendations.',
                timestamp: new Date().toISOString(),
              });
              posthog?.capture(TrackingIds.events.REPORT_GENERATED);
            } else if (data.type === 'adaptation_projects_ready') {
              // ENG-406: projects generated by Haven-AI and persisted to the backend
              addNotification({
                message: data.message || 'Your climate resilience projects are ready! Click to view them.',
                timestamp: data.timestamp ? new Date(data.timestamp * 1000).toISOString() : new Date().toISOString(),
              });
            } else if (data.type === 'profile_updated') {
              addNotification({
                message: data.message,
                timestamp: data.timestamp ? new Date(data.timestamp * 1000).toISOString() : new Date().toISOString(),
              });
            } else if (data.type === 'household_details_updated') {
              addNotification({
                message: data.message,
                timestamp: data.timestamp ? new Date(data.timestamp * 1000).toISOString() : new Date().toISOString(),
              });
            } else if (data.type === 'general') {
              addNotification({
                message: data.message,
                timestamp: data.timestamp || new Date().toISOString(),
              });
            } else {
              // Only add fallback notification if we have a meaningful message
              if (data.message && data.type !== 'ping' && data.type !== 'connected') {
                console.log('Unknown notification type:', data.type, 'Adding as general notification');
                addNotification({
                  message: data.message,
                  timestamp: data.timestamp ? new Date(data.timestamp * 1000).toISOString() : new Date().toISOString(),
                });
              }
            }
          } catch (error) {
            console.error('Error parsing notification data:', error);
          }
        };

        eventSource.onerror = (error) => {
          // Only log errors in development mode to reduce noise
          if (import.meta.env.MODE === 'development') {
            console.error('EventSource failed:', error);
            if (eventSource) {
              console.error('EventSource readyState:', eventSource.readyState);
              console.error('EventSource URL:', eventSource.url);
            }
          }
          setIsConnected(false);
        };

      } catch (error) {
        // Only log connection errors in development mode
        if (import.meta.env.MODE === 'development') {
          console.error('Error connecting to notification stream:', error);
        }
        setIsConnected(false);
      }
    };

    connectToNotifications();

    return () => {
      if (eventSource) {
        eventSource.close();
        setIsConnected(false);
      }
    };
  }, [isLoggedIn, accessToken, addNotification]);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
    isConnected,
    requestPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
